import { PropsWithChildren, useMemo, useState } from 'react';
import { Text } from '@mantine/core';
import {
	SecurityFeatureBaseProps,
	buildSecurityRankingDeepLink,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/securityFeatures.utils.ts';
import {
	useTranslationCapManage,
	useTranslationCommonUi,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { EditableInfoContainer } from '@dealstack/frontend/shared/components/EditableInfoContainer/EditableInfoContainer.tsx';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { FormProvider } from 'react-hook-form';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader.tsx';
import { EditableInfoContainerGrid } from '@dealstack/frontend/shared/components/EditableInfoContainer/styled.tsx';
import { useMutation } from '@tanstack/react-query';
import {
	deleteSecurityLiquidationPreference,
	updateSecurityLiquidationPreference,
} from '@dealstack/frontend/shared/dataExchange/services/securities.ts';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import {
	LIQUIDATION_PREFERENCE_FORM_FIELD,
	useLiquidationPreferenceController,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/LiquidationPreference/useLiquidationPreferenceController.ts';
import { SecurityLiquidationPreferenceFormDto } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/LiquidationPreference/liquidationPreference.zod.ts';
import { DetailEditableField } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/DetailEditableField.tsx';
import { NumberInput, Select } from 'react-hook-form-mantine';
import {
	CreateUpdateSecurityLiquidationPreferenceDto,
	SecurityFeatureType,
	SecurityInterestDefinitionPrincipal,
	SecurityLiquidationPreferenceRes,
} from '@dealstack/shared/lib/generated';
import { EditableInfoField } from '@dealstack/frontend/shared/components/EditableInfoField/EditableInfoField.tsx';
import { SecurityRankField } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityRankField/SecurityRankField.tsx';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import { useNavigate } from 'react-router-dom';
import {
	getSecurityPrincipalTranslation,
	makeSecurityPrincipalDropdownOptions,
} from '@dealstack/frontend/shared/incomeRights.ts';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import { convertCurrencyToSymbol } from '@dealstack/shared/lib/format/currency/currency.ts';

interface LiquidationPreferenceProps extends SecurityFeatureBaseProps {
	/** Null when no LP is set. */
	liquidationPreference: SecurityLiquidationPreferenceRes | null;
	/** Call after a write so the displayed value stays current. */
	refetchLiquidationPreference?: () => void;
}

export const LiquidationPreference = ({
	security,
	onCancel,
	refreshDetails,
	hasEditorPermission = true,
	relativeRanksMap,
	liquidationPreference,
	refetchLiquidationPreference,
}: PropsWithChildren<LiquidationPreferenceProps>) => {
	const tCapManage = useTranslationCapManage();
	const tCommonUI = useTranslationCommonUi();
	const { liquidationPreferenceForm, resetForm } =
		useLiquidationPreferenceController(liquidationPreference);
	const [isEdit, setIsEdit] = useState(!liquidationPreference);
	const navigate = useNavigate();
	const navigateToRanking = () =>
		navigate(
			buildSecurityRankingDeepLink(security?.id, SecurityFeatureType.LIQUIDATION_PREFERENCE)
		);

	const deleteRequest = useMutation({ mutationFn: deleteSecurityLiquidationPreference });
	const updateRequest = useMutation({ mutationFn: updateSecurityLiquidationPreference });

	const isLoading = updateRequest.isPending || deleteRequest.isPending;

	const principal = liquidationPreferenceForm.watch(LIQUIDATION_PREFERENCE_FORM_FIELD.principal);
	const disablePrincipalValue =
		principal === SecurityInterestDefinitionPrincipal.ISSUE_COST_PER_UNIT;
	const inputDisabledStyles = {
		input: {
			color: 'transparent !important',
		},
	};

	const principalDropdownOptions = makeSecurityPrincipalDropdownOptions(tCommonUI);

	const getPrincipalValuePlaceholder = (): string => {
		switch (principal) {
			case SecurityInterestDefinitionPrincipal.ISSUE_COST_PER_UNIT:
				return tCapManage('securities.interest_definition.principal.issue_cost_per_unit');
			case SecurityInterestDefinitionPrincipal.VALUE_PER_UNIT:
				return tCapManage('securities.interest_definition.principal.value_per_unit');
			case SecurityInterestDefinitionPrincipal.VALUE_FOR_TOTAL_ISSUE:
				return tCapManage('securities.interest_definition.principal.value_for_total_issue');
			default:
				return '';
		}
	};

	const securityRank = useMemo(() => {
		if (!security) {
			return null;
		}

		const matchedSecRank = security.securityRanks?.find(
			(secRank) => secRank.featureType === SecurityFeatureType.LIQUIDATION_PREFERENCE
		);

		return matchedSecRank
			? {
					...matchedSecRank,
					relativeSortOrder: relativeRanksMap?.get(matchedSecRank.rankId)
						?.relativeSortOrder,
				}
			: null;
	}, [security, relativeRanksMap]);

	const handleUpdate = (args: SecurityLiquidationPreferenceFormDto) => {
		const securityId = security?.id ?? '';

		const body: CreateUpdateSecurityLiquidationPreferenceDto = {
			principal: args.principal,
			principalValue: args.principalValue ?? null,
		};

		updateRequest.mutate(
			{ securityId, body },
			{
				onSuccess: () => {
					showToastV2({
						title: tCapManage(
							'toasts.success.liquidation_preference_updated_successfully'
						),
					});
					refetchLiquidationPreference?.();
					refreshDetails?.(securityId);
					setIsEdit(false);
				},
			}
		);
	};

	const handleDelete = () => {
		const securityId = security?.id ?? '';
		deleteRequest.mutate(securityId, {
			onSuccess: () => {
				showToastV2({
					title: tCapManage('toasts.success.liquidation_preference_deleted_successfully'),
				});
				refetchLiquidationPreference?.();
				refreshDetails?.(securityId);
				setIsEdit(false);
			},
		});
	};

	const handleCancel = () => {
		resetForm();
		onCancel?.();
	};

	return (
		<EditableInfoContainer
			title={tCapManage('securities.liquidation_preference.title')}
			isEdit={isEdit}
			setIsEdit={setIsEdit}
			disableButtons={isLoading}
			onUpdate={liquidationPreferenceForm.handleSubmit(handleUpdate)}
			onRemove={handleDelete}
			onCancel={handleCancel}
			hideRemove={!liquidationPreference?.id}
			hasEditorPermission={hasEditorPermission}
			updateBtnDataTestId={TEST_ELEMENT_IDS.CAP_SECURITY_DETAILS_FEATURE_ROW_UPDATE_BUTTON}
		>
			<FormProvider {...liquidationPreferenceForm}>
				{isLoading && <InlineLoader />}

				<form
					onSubmit={liquidationPreferenceForm.handleSubmit(handleUpdate)}
					style={{ width: '100%' }}
				>
					<EditableInfoContainerGrid $gridColumns={4} $alignItems="start">
						<DetailEditableField
							editMode={isEdit}
							label={tCapManage('securities.liquidation_preference.principal.label')}
							formInput={
								<Select
									name={LIQUIDATION_PREFERENCE_FORM_FIELD.principal}
									control={liquidationPreferenceForm.control}
									size="md"
									rightSection={<Icon variant="caret-down" size="lg" />}
									rightSectionProps={{
										style: { pointerEvents: 'none' },
									}}
									data={principalDropdownOptions.map((o) => ({
										value: o.value,
										label: o.label ?? '',
									}))}
									data-testid="cap-liquidation-preference-principal-type"
								/>
							}
							data={getSecurityPrincipalTranslation(
								tCommonUI,
								liquidationPreference?.principal
							)}
						/>

						<DetailEditableField
							editMode={isEdit}
							label={tCapManage(
								'securities.liquidation_preference.principal_value.label'
							)}
							formInput={
								<NumberInput
									name={LIQUIDATION_PREFERENCE_FORM_FIELD.principalValue}
									control={liquidationPreferenceForm.control}
									placeholder={getPrincipalValuePlaceholder()}
									decimalScale={2}
									disabled={disablePrincipalValue}
									styles={disablePrincipalValue ? inputDisabledStyles : undefined}
									size="md"
									leftSection={
										<Text size="lg" c="#7b8991">
											{convertCurrencyToSymbol(security?.currency)}
										</Text>
									}
									data-testid="cap-liquidation-preference-principal-value"
									hideControls
								/>
							}
							data={
								disablePrincipalValue ||
								typeof liquidationPreference?.principalValue !== 'number'
									? LONG_DASH
									: liquidationPreference?.principalValue
							}
						/>

						<EditableInfoField
							editMode={isEdit}
							label={tCapManage('securities.liquidation_preference.rank.label')}
							formInput={
								<SecurityRankField
									unranked={!securityRank}
									rankName={securityRank?.rank.displayName}
									sortOrder={securityRank?.relativeSortOrder || 1}
									totalRanks={relativeRanksMap?.size}
									onClick={navigateToRanking}
								/>
							}
							customValueElement={
								<SecurityRankField
									unranked={!securityRank}
									rankName={securityRank?.rank.displayName}
									sortOrder={securityRank?.relativeSortOrder || 1}
									totalRanks={relativeRanksMap?.size}
									showContainer={false}
								/>
							}
						/>
					</EditableInfoContainerGrid>
				</form>
			</FormProvider>
		</EditableInfoContainer>
	);
};
